import { toLocalDateOnly, convertToTimezone } from "@gigpro/utils/datetime";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { produce } from "immer";
import {
  hireLocationsDirectHireCreate,
  useHireJobsApplicantsRetrieve,
  useHireJobsApplicantsTimelineRetrieve,
} from "../../generated/job-candidates/job-candidates";
import type { CandidateTimeline } from "../../generated/model/candidateTimeline";
import type { DirectHireCreateRequest } from "../../generated/model/directHireCreateRequest";
import type { JobApplicantDetailResponse } from "../../generated/model/jobApplicantDetailResponse";
import { type DateOnlyInputs, composeSelect, toDateOnlyFields } from "../types";

export const zoneJobApplicant = produce<JobApplicantDetailResponse>((draft) => {
  const interview = draft.screening_interview;
  if (!interview) return;
  interview.start_time = convertToTimezone(interview.start_time, interview.timezone);
  interview.end_time = convertToTimezone(interview.end_time, interview.timezone);
});

export function useHireJobsApplicantsRetrieveWithTransform<TData = JobApplicantDetailResponse>(
  ...[jobPostingId, id, options]: Parameters<typeof useHireJobsApplicantsRetrieve<TData>>
) {
  return useHireJobsApplicantsRetrieve<TData>(jobPostingId, id, {
    ...options,
    query: { ...options?.query, select: composeSelect(zoneJobApplicant, options?.query?.select) },
  });
}

// Consumer's isPast check reads back the same fallback, so a gig-less interview's null tz must resolve
// to the location tz here too — otherwise zoning and the past-check disagree by the offset.
export const zoneTimeline = (locationTimezone: string | null | undefined) =>
  produce<CandidateTimeline>((draft) => {
    for (const interview of [...draft.interviews, ...draft.screening_interviews]) {
      const timezone = interview.timezone ?? locationTimezone;
      interview.start_time = convertToTimezone(interview.start_time, timezone);
      interview.end_time = convertToTimezone(interview.end_time, timezone);
    }
  });

export function useHireJobsApplicantsTimelineRetrieveWithTransform<TData = CandidateTimeline>(
  jobPostingId: Parameters<typeof useHireJobsApplicantsTimelineRetrieve<TData>>[0],
  id: Parameters<typeof useHireJobsApplicantsTimelineRetrieve<TData>>[1],
  locationTimezone: string | null | undefined,
  options?: Parameters<typeof useHireJobsApplicantsTimelineRetrieve<TData>>[2],
) {
  return useHireJobsApplicantsTimelineRetrieve<TData>(jobPostingId, id, {
    ...options,
    query: { ...options?.query, select: composeSelect(zoneTimeline(locationTimezone), options?.query?.select) },
  });
}

type DirectHireCreateVariables = { locationId: number; data: DateOnlyInputs<DirectHireCreateRequest, "expires_at"> };

export function useHireLocationsDirectHireCreateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof hireLocationsDirectHireCreate>>, unknown, DirectHireCreateVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: ({ locationId, data }: DirectHireCreateVariables) =>
      hireLocationsDirectHireCreate(locationId, toDateOnlyFields(data, ["expires_at"], toLocalDateOnly)),
  });
}
